import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useCategories } from "../hooks/useCatalog";
import { resolvePhotoUrl } from "../lib/photos";

const CategoryGrid = () => {
    const { data: categories = [], isLoading } = useCategories();
    const [expanded, setExpanded] = useState(false);

    return (
        <section id="categories" className="relative bg-[#F5F5F7] py-14 md:py-20 overflow-hidden">
            <div className="max-w-[1320px] mx-auto px-4 sm:px-6 md:px-10">
                <div className="flex flex-col items-center text-center">
                    <span className="dp-eyebrow">Nos univers</span>
                    <h2 className="mt-3 font-display text-3xl md:text-[2.6rem] font-semibold tracking-tight text-[#1C1C1E]">
                        Explorez nos catégories
                    </h2>
                    <p className="mt-3 max-w-xl text-[14px] md:text-[15px] leading-relaxed text-neutral-500">
                        <strong className="font-semibold text-neutral-800">Bienvenue chez Atelier AX</strong>
                        , votre adresse dédiée aux accessoires mobiles de qualité.
                        {" "}Découvrez une sélection soigneusement choisie de{" "}
                        <strong className="font-semibold text-neutral-800">
                            pochettes haut de gamme, chargeurs, câbles, supports voiture et bien plus
                        </strong>
                        {/* Texte complet conservé dans le DOM (collapse CSS) pour le SEO :
                            il reste présent dans le HTML prerendu, masqué par défaut. */}
                        <span className={expanded ? "" : "hidden"}>
                            , avec des nouveautés et des conseils adaptés à vos besoins.{" "}
                            <strong className="font-semibold text-neutral-800">
                                Découvrez notre collection en ligne ou en boutique.
                            </strong>
                        </span>
                    </p>
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        aria-expanded={expanded}
                        className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-neutral-800 underline underline-offset-4 decoration-black/20 hover:text-[#d4af37] hover:decoration-[#d4af37]/50 transition-colors"
                    >
                        {expanded ? (
                            <>
                                Lire moins
                                <ChevronUp className="w-3.5 h-3.5" />
                            </>
                        ) : (
                            <>
                                Lire plus
                                <ChevronDown className="w-3.5 h-3.5" />
                            </>
                        )}
                    </button>
                </div>

                {isLoading ? (
                    <div className="mt-12 flex items-center justify-center gap-3 text-neutral-500">
                        <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
                        <span className="text-sm font-medium">Chargement des catégories…</span>
                    </div>
                ) : (
                    <div className="mt-10 md:mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
                        {categories.map((c, i) => {
                            const src = resolvePhotoUrl(c.photo);
                            return (
                                <motion.div
                                    key={c.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "100px" }}
                                    transition={{ duration: 0.35, delay: (i % 5) * 0.05 }}
                                >
                                    <Link
                                        to={`/categorie/${encodeURIComponent(c.id)}`}
                                        className="group block"
                                    >
                                        <div className="relative aspect-[5/6] overflow-hidden rounded-2xl bg-white border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-300 group-hover:-translate-y-1 group-hover:border-[#d4af37]/40 group-hover:shadow-[0_14px_34px_-10px_rgba(0,0,0,0.2)]">
                                            {src ? (
                                                <img
                                                    src={src}
                                                    alt={c.label}
                                                    loading="lazy"
                                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                                                />
                                            ) : (
                                                <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-white via-[#FBFBFD] to-[#EFEFF4]">
                                                    <span className="font-display text-5xl font-semibold text-[#1C1C1E]/10 select-none">
                                                        {c.label.charAt(0)}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/[0.05] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                        </div>
                                        <div className="mt-2.5 md:mt-3 pb-1 text-center">
                                            <h3 className="font-display text-[13px] md:text-[14.5px] font-semibold tracking-tight text-[#1C1C1E]">
                                                {c.label}
                                            </h3>
                                            <span className="mt-0.5 block font-mono-tech text-[9.5px] md:text-[10px] uppercase tracking-[0.18em] text-neutral-400 transition-colors duration-300 group-hover:text-[#d4af37]">
                                                Découvrir
                                            </span>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

export default CategoryGrid;
